import React from 'react';
import { BarChart3 } from 'lucide-react';

/**
 * Small SVG weight-trend line chart.
 * Props:
 *   entries: array of { date: 'YYYY-MM-DD', weight_lbs: number, notes?: string }
 *   days:    lookback window in days (7 | 30 | 60 | 90)
 *   isPremium: boolean — enables 3-point moving average + goal-line
 *   goalWeight: optional number — draws a dashed goal line when in-range
 */
export default function WeightTrendChart({ entries, days, isPremium, goalWeight }) {
  const raw = Array.isArray(entries)
    ? entries
        .filter(e => (e?.notes || '').toLowerCase() !== 'initial weight entry')
        .slice()
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    : [];

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const filtered = raw.filter(e => new Date(e.date) >= cutoff);
  const data = filtered.length ? filtered : raw.slice(-Math.min(days, raw.length));

  if (!data || data.length < 2) {
    return (
      <div className="chart-empty">
        <BarChart3 size={26} strokeWidth={1.6} />
        <p>Log at least 2 weigh-ins to see your trend chart.</p>
      </div>
    );
  }

  const width = 320, height = 140, padding = 24;
  const values = data.map(d => d.weight_lbs);
  const min = Math.min(...values) - 1;
  const max = Math.max(...values) + 1;
  const range = Math.max(max - min, 1);
  const stepX = (width - padding * 2) / Math.max(data.length - 1, 1);
  const points = data.map((d, i) => {
    const x = padding + i * stepX;
    const y = padding + ((max - d.weight_lbs) / range) * (height - padding * 2);
    return [x, y];
  });
  const path = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ');
  const areaPath = `${path} L${points[points.length - 1][0]},${height - padding} L${padding},${height - padding} Z`;

  let mavPath = '';
  if (isPremium && data.length >= 3) {
    const window = 3;
    const mavPoints = data.map((_, i) => {
      const slice = data.slice(Math.max(0, i - window + 1), i + 1);
      const avg = slice.reduce((s, d) => s + d.weight_lbs, 0) / slice.length;
      const x = padding + i * stepX;
      const y = padding + ((max - avg) / range) * (height - padding * 2);
      return [x, y];
    });
    mavPath = mavPoints.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ');
  }

  let goalLine = null;
  if (isPremium && goalWeight && goalWeight >= min && goalWeight <= max) {
    const y = padding + ((max - goalWeight) / range) * (height - padding * 2);
    goalLine = { y, label: `Goal: ${goalWeight} lbs` };
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="progress-chart-svg" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="chartFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#1E90FF" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#1E90FF" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#chartFill)" />
      <path d={path} fill="none" stroke="#1E90FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {mavPath && (
        <path d={mavPath} fill="none" stroke="#0A2A5E" strokeWidth="1.5" strokeDasharray="4 3" strokeLinecap="round" />
      )}
      {goalLine && (
        <>
          <line x1={padding} x2={width - padding} y1={goalLine.y} y2={goalLine.y} stroke="#22c55e" strokeWidth="1.2" strokeDasharray="3 3" />
          <text x={width - padding} y={goalLine.y - 4} textAnchor="end" fontSize="9" fill="#22c55e" fontWeight="600">{goalLine.label}</text>
        </>
      )}
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2.5" fill="#0A2A5E" />
      ))}
      <text x={padding} y={height - 6} fontSize="9" fill="#8f8f8f">{data[0]?.date?.slice(5)}</text>
      <text x={width - padding} y={height - 6} textAnchor="end" fontSize="9" fill="#8f8f8f">{data[data.length - 1]?.date?.slice(5)}</text>
    </svg>
  );
}
